"use client";

import { useEffect, useRef } from "react";
import { useLanguage } from "@/lib/i18n/language-provider";

interface DynamicTitleProps {
  width: number;
  height: number;
  isAnimating: boolean;
}

export function DynamicTitle({ width, height, isAnimating }: DynamicTitleProps) {
  const { t } = useLanguage();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLiveRef = useRef(false);
  
  // Fonction pour mettre à jour le titre
  const updateTitle = () => {
    if (isAnimating) {
      isLiveRef.current = !isLiveRef.current;
      const prefix = isLiveRef.current ? "LIVE" : "VisualGrid";
      document.title = `${prefix} - ${width} × ${height}`;
    } else {
      document.title = `VisualGrid - ${width} × ${height}`;
    }
  };
  
  useEffect(() => {
    // Mise à jour initiale du titre
    updateTitle();
    
    // Si l'animation est active, configurer l'intervalle
    if (isAnimating) {
      intervalRef.current = setInterval(updateTitle, 2000);
    }
    
    // Nettoyage
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.title = "VisualGrid";
    };
  }, [width, height, isAnimating]);
  
  // Effet pour gérer les changements d'état d'animation
  useEffect(() => {
    // Nettoyer l'intervalle existant
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Si l'animation est active, créer un nouvel intervalle
    if (isAnimating) {
      updateTitle(); // Mise à jour immédiate
      intervalRef.current = setInterval(updateTitle, 2000);
    } else {
      // Réinitialiser le titre si l'animation s'arrête
      isLiveRef.current = false;
      document.title = `VisualGrid - ${width} × ${height}`;
    }
    
    // Nettoyage
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAnimating]);
  
  // Ce composant ne rend rien visuellement
  return null;
} 